import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Timer, Plus, Trash, Clock } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function PayoutScheduling() {
  var [schedules, setSchedules] = useState([]);
  var [history, setHistory] = useState([]);
  var [loading, setLoading] = useState(true);
  var [dialogOpen, setDialogOpen] = useState(false);
  var [form, setForm] = useState({ affiliate_id: '', interval: 'monthly', min_balance: 50 });

  var fetchData = function () {
    Promise.all([api.get('/scheduling/schedules'), api.get('/scheduling/history')])
      .then(function (r) { setSchedules(r[0].data); setHistory(r[1].data); })
      .catch(console.error).finally(function () { setLoading(false); });
  };

  useEffect(function () { fetchData(); }, []);

  var createSchedule = async function (e) {
    e.preventDefault();
    try {
      await api.post('/scheduling/schedules', {
        affiliate_id: form.affiliate_id || null,
        interval: form.interval,
        min_balance: parseFloat(form.min_balance) || 50
      });
      toast.success('Schedule created');
      setDialogOpen(false);
      setForm({ affiliate_id: '', interval: 'monthly', min_balance: 50 });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create schedule'); }
  };

  var toggleSchedule = async function (id, enabled) {
    try {
      await api.put('/scheduling/schedules/' + id, { enabled: !enabled });
      toast.success(enabled ? 'Schedule paused' : 'Schedule resumed');
      fetchData();
    } catch (err) { toast.error('Failed to update'); }
  };

  var deleteSchedule = async function (id) {
    try {
      await api.delete('/scheduling/schedules/' + id);
      toast.success('Schedule deleted');
      fetchData();
    } catch (err) { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="payout-scheduling">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight flex items-center gap-3">
            <Timer size={32} className="text-blue-400" weight="duotone" />
            Payout Scheduling
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Automated cron-based payout processing</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-schedule-btn" className="bg-blue-600 hover:bg-blue-700 text-white gap-2"><Plus size={16} /> New Schedule</Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
            <DialogHeader><DialogTitle className="font-heading text-white">Create Payout Schedule</DialogTitle></DialogHeader>
            <form onSubmit={createSchedule} className="space-y-4">
              <div>
                <Label className="text-zinc-300">Affiliate ID (empty = all affiliates)</Label>
                <Input data-testid="schedule-affiliate" value={form.affiliate_id} onChange={function (e) { setForm(function (p) { return Object.assign({}, p, { affiliate_id: e.target.value }); }); }} placeholder="Leave empty for all" className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100" />
              </div>
              <div>
                <Label className="text-zinc-300">Interval</Label>
                <select data-testid="schedule-interval" value={form.interval} onChange={function (e) { setForm(function (p) { return Object.assign({}, p, { interval: e.target.value }); }); }} className="mt-1 w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-zinc-100 text-sm">
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Bi-weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              <div>
                <Label className="text-zinc-300">Minimum Balance ($)</Label>
                <Input data-testid="schedule-min-balance" type="number" step="0.01" value={form.min_balance} onChange={function (e) { setForm(function (p) { return Object.assign({}, p, { min_balance: e.target.value }); }); }} className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100" />
              </div>
              <Button data-testid="submit-schedule" type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white">Create Schedule</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-3"><CardTitle className="font-heading text-lg text-white">Active Schedules</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">ID</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Affiliate</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Interval</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Min Balance</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Next Run</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Payouts</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Status</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {schedules.map(function (s, i) {
                return (
                  <TableRow key={i} className="border-zinc-800/50" data-testid={'schedule-row-' + i}>
                    <TableCell className="text-zinc-500 font-mono text-xs">{s.schedule_id}</TableCell>
                    <TableCell className="text-zinc-200">{s.affiliate_id || 'All'}</TableCell>
                    <TableCell><Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20 text-[10px]">{s.interval}</Badge></TableCell>
                    <TableCell className="text-zinc-300 font-mono">${s.min_balance}</TableCell>
                    <TableCell className="text-zinc-400 text-xs">{s.next_run ? new Date(s.next_run).toLocaleDateString() : '-'}</TableCell>
                    <TableCell className="text-zinc-300">{s.total_payouts || 0} (${(s.total_amount || 0).toLocaleString()})</TableCell>
                    <TableCell>
                      <Badge className={s.enabled ? 'bg-emerald-500/10 text-emerald-400 text-[10px]' : 'bg-zinc-700/30 text-zinc-400 text-[10px]'}>
                        {s.enabled ? 'Active' : 'Paused'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={function () { toggleSchedule(s.schedule_id, s.enabled); }} data-testid={'toggle-schedule-' + i} className="text-zinc-400 hover:text-blue-400 text-xs">
                          {s.enabled ? 'Pause' : 'Resume'}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={function () { deleteSchedule(s.schedule_id); }} data-testid={'delete-schedule-' + i} className="text-zinc-400 hover:text-red-400">
                          <Trash size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {schedules.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-zinc-500 py-8">No payout schedules configured.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-3"><CardTitle className="font-heading text-lg text-white flex items-center gap-2"><Clock size={20} className="text-emerald-400" /> Scheduled Payout History</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Payout ID</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Affiliate</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Amount</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Status</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.slice(0, 20).map(function (h, i) {
                return (
                  <TableRow key={i} className="border-zinc-800/50">
                    <TableCell className="text-zinc-500 font-mono text-xs">{h.payout_id}</TableCell>
                    <TableCell className="text-zinc-200">{h.affiliate_name}</TableCell>
                    <TableCell className="text-emerald-400 font-mono">${(h.amount || 0).toLocaleString()}</TableCell>
                    <TableCell><Badge className="bg-blue-500/10 text-blue-400 text-[10px]">{h.status}</Badge></TableCell>
                    <TableCell className="text-zinc-500 text-xs">{h.created_at ? new Date(h.created_at).toLocaleString() : '-'}</TableCell>
                  </TableRow>
                );
              })}
              {history.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-zinc-500 py-8">No scheduled payouts processed yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
